import { useEffect, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useScrollLock } from '../../hooks'
import styles from './Lightbox.module.css'

interface LightboxImage {
  src: string
  alt: string
}

interface LightboxProps {
  images: LightboxImage[]
  currentIndex: number
  isOpen: boolean
  onClose: () => void
  onNavigate: (index: number) => void
}

export function Lightbox({ images, currentIndex, isOpen, onClose, onNavigate }: LightboxProps) {
  const [direction, setDirection] = useState(0)

  const navigate = useCallback((newIndex: number) => {
    if (newIndex < 0 || newIndex >= images.length) return
    setDirection(newIndex > currentIndex ? 1 : -1)
    onNavigate(newIndex)
  }, [currentIndex, images.length, onNavigate])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
    if (e.key === 'ArrowLeft') navigate(currentIndex - 1)
    if (e.key === 'ArrowRight') navigate(currentIndex + 1)
  }, [onClose, navigate, currentIndex])

  useScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  const slideVariants = {
    enter: (d: number) => ({ x: d > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: d > 0 ? -300 : 300, opacity: 0 }),
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label="Galería de imágenes"
        >
          <div className={styles.imageContainer} onClick={(e) => e.stopPropagation()}>
            <AnimatePresence mode="wait" custom={direction}>
              <motion.img
                key={currentIndex}
                src={images[currentIndex].src}
                alt={images[currentIndex].alt}
                className={styles.image}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={(_, info) => {
                  if (info.offset.x < -100) navigate(currentIndex + 1)
                  else if (info.offset.x > 100) navigate(currentIndex - 1)
                }}
              />
            </AnimatePresence>
          </div>

          {currentIndex > 0 && (
            <button
              className={`${styles.navButton} ${styles.prevButton}`}
              onClick={(e) => { e.stopPropagation(); navigate(currentIndex - 1) }}
              aria-label="Imagen anterior"
            >
              &#8249;
            </button>
          )}

          {currentIndex < images.length - 1 && (
            <button
              className={`${styles.navButton} ${styles.nextButton}`}
              onClick={(e) => { e.stopPropagation(); navigate(currentIndex + 1) }}
              aria-label="Imagen siguiente"
            >
              &#8250;
            </button>
          )}

          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Cerrar galería"
          >
            &#10005;
          </button>

          <span className={styles.counter}>
            {currentIndex + 1} / {images.length}
          </span>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}
